import {
  DEFAULT_UTC_OFFSET,
  civilDayDiffInOffset,
  formatDayMonthInOffset,
  formatHourMinuteInOffset,
  formatWeekdayShortInOffset
} from '@/utils/date'
import type {
  PriorityConfig,
  PriorityPaymentRow,
  PrioritySituation,
  PrioritySlaBand,
  PrioritySlaDto,
  PrioritySlaKind,
  PrioritySlaState,
  PrioritySlaThresholds
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

/**
 * Regras de EXIBIÇÃO do SLA (spec §8.2/§8.3). A regra de negócio (prazo,
 * etapa, principal/secundário, ordem) é do backend; aqui só se reclassifica o
 * estado no tique e se escreve o texto do M3.
 */

export const PRIORITY_SLA_STATE_LABELS: Record<PrioritySlaState, string> = {
  OVERDUE: 'Estourado',
  URGENT: 'Urgente',
  ATTENTION: 'Atenção',
  ON_TIME: 'No prazo',
  DONE: 'Concluído',
  NOT_STARTED: 'Não iniciado'
}

/** Cor do texto por estado (contador do SLA e relógio do ciclo, M3). */
export const PRIORITY_SLA_TEXT_CLASS: Record<PrioritySlaState, string> = {
  OVERDUE: 'text-red-700',
  URGENT: 'text-orange-700',
  ATTENTION: 'text-amber-700',
  ON_TIME: 'text-textPrimary',
  DONE: 'text-green-700',
  NOT_STARTED: 'text-neutral50'
}

export const PRIORITY_SLA_PENDING_STATES: readonly PrioritySlaState[] = [
  'OVERDUE',
  'URGENT',
  'ATTENTION',
  'ON_TIME'
]

export const isPendingSlaState = (state: PrioritySlaState): boolean =>
  PRIORITY_SLA_PENDING_STATES.includes(state)

/** Padrões do contrato, só para textos quando o `config` vier sem limiares. */
const DEFAULT_THRESHOLDS: Record<PrioritySlaKind, PrioritySlaThresholds> = {
  IN_CASH: { attentionMinutes: 60, urgentMinutes: 15 },
  ANTECIPE: { attentionMinutes: 360, urgentMinutes: 120 }
}

const MINUTE_MS = 60_000

const pad = (n: number): string => String(n).padStart(2, '0')

export interface PriorityLiveSla {
  state: PrioritySlaState
  remainingSeconds: number | null
}

/** Bordas inclusivas em ms, como `resolveSlaState` do BE (spec §3.5). */
export const stateForRemaining = (
  remainingMs: number,
  thresholds: PrioritySlaThresholds
): PrioritySlaState => {
  if (remainingMs <= 0) return 'OVERDUE'
  if (remainingMs <= thresholds.urgentMinutes * MINUTE_MS) return 'URGENT'
  if (remainingMs <= thresholds.attentionMinutes * MINUTE_MS) return 'ATTENTION'
  return 'ON_TIME'
}

/**
 * Estado e restante "agora". Só os quatro estados pendentes andam no tique;
 * sem `nowMs` (antes da 1ª resposta) ou sem limiares (BE anterior ao SLA),
 * vale o que o servidor mandou.
 */
export const resolveLiveSla = (
  sla: PrioritySlaDto,
  nowMs: number | null,
  thresholds: PrioritySlaThresholds | null
): PriorityLiveSla => {
  if (!isPendingSlaState(sla.state)) return { state: sla.state, remainingSeconds: null }
  const deadlineMs = Date.parse(sla.deadlineAt)
  if (nowMs === null || !thresholds || !Number.isFinite(deadlineMs)) {
    return { state: sla.state, remainingSeconds: sla.remainingSeconds }
  }
  const remainingMs = deadlineMs - nowMs
  return {
    state: stateForRemaining(remainingMs, thresholds),
    remainingSeconds: Math.floor(remainingMs / 1000)
  }
}

export const priorityThresholdsFor = (
  config: PriorityConfig | null | undefined,
  kind: PrioritySlaKind
): PrioritySlaThresholds | null => config?.thresholds?.[kind] ?? null

/** Estado do SLA principal da linha, ao vivo; `null` = linha sem SLA (BE antigo). */
export const priorityRowLiveState = (
  row: Pick<PriorityPaymentRow, 'sla'>,
  nowMs: number | null,
  config: PriorityConfig | null | undefined
): PrioritySlaState | null => {
  if (!row.sla) return null
  return resolveLiveSla(row.sla, nowMs, priorityThresholdsFor(config, row.sla.kind)).state
}

const splitDuration = (seconds: number) => {
  let rest = Math.abs(Math.trunc(seconds))
  const days = Math.floor(rest / 86_400)
  rest %= 86_400
  return {
    days,
    hours: Math.floor(rest / 3_600),
    minutes: Math.floor((rest % 3_600) / 60),
    seconds: rest % 60
  }
}

/** `mm:ss` · `HH:mm:ss` · `Xd HH:mm:ss`; `+` quando estourado. */
export const formatSlaCountdown = (seconds: number): string => {
  const d = splitDuration(seconds)
  const clock = `${pad(d.minutes)}:${pad(d.seconds)}`
  let body = clock
  if (d.days) body = `${d.days}d ${pad(d.hours)}:${clock}`
  else if (d.hours) body = `${pad(d.hours)}:${clock}`
  return seconds < 0 ? `+${body}` : body
}

/** `N min` (mínimo 1) · `Nh` · `NhMM` · `Nd Nh`. */
export const formatSlaShortDuration = (seconds: number): string => {
  const d = splitDuration(seconds)
  if (d.days) return `${d.days}d ${d.hours}h`
  if (d.hours) return d.minutes ? `${d.hours}h${pad(d.minutes)}` : `${d.hours}h`
  return `${Math.max(d.minutes, 1)} min`
}

/** `'24/09 10:00'` em Brasília; `'—'` se o instante for inválido. */
export const formatSlaDateTime = (iso: string): string => {
  const day = formatDayMonthInOffset(iso, DEFAULT_UTC_OFFSET)
  const hour = formatHourMinuteInOffset(iso, DEFAULT_UTC_OFFSET)
  return day && hour ? `${day} ${hour}` : '—'
}

export const formatSlaHour = (iso: string): string =>
  formatHourMinuteInOffset(iso, DEFAULT_UTC_OFFSET) ?? '—'

/** Só a hora quando cai no mesmo dia (Brasília) da referência; senão `dd/mm HH:mm`. */
export const formatSlaStamp = (iso: string, referenceMs: number | null): string => {
  if (referenceMs === null) return formatSlaDateTime(iso)
  const reference = formatDayMonthInOffset(new Date(referenceMs).toISOString(), DEFAULT_UTC_OFFSET)
  return formatDayMonthInOffset(iso, DEFAULT_UTC_OFFSET) === reference
    ? formatSlaHour(iso)
    : formatSlaDateTime(iso)
}

/** Até quantos dias à frente o prazo sai com o dia da semana (`'seg 11:00'`). */
const WEEKDAY_STAMP_MAX_DAYS = 6

/**
 * Prazo com o dia quando não é hoje (B1, 25/09), em Brasília: `'11:00'` no mesmo
 * dia da referência; `'seg 11:00'` de 1 a 6 dias à frente; senão `dd/mm HH:mm`
 * (`formatSlaStamp`). Sem referência (antes da 1ª resposta), só a hora.
 */
export const formatSlaDeadlineStamp = (iso: string, referenceMs: number | null): string => {
  if (referenceMs === null) return formatSlaHour(iso)
  const diff = civilDayDiffInOffset(iso, new Date(referenceMs).toISOString(), DEFAULT_UTC_OFFSET)
  const weekday = formatWeekdayShortInOffset(iso, DEFAULT_UTC_OFFSET)
  if (diff !== null && diff >= 1 && diff <= WEEKDAY_STAMP_MAX_DAYS && weekday) {
    return `${weekday} ${formatSlaHour(iso)}`
  }
  return formatSlaStamp(iso, referenceMs)
}

/** Rótulo do SLA do Antecipe concluído por pagamento (A1, 25/09): sem hora. */
export const PRIORITY_ANTECIPE_PAID_DONE_LABEL = `${PRIORITY_SLA_STATE_LABELS.DONE} · Antecipe pago`

export const formatSlaChipText = (
  live: PriorityLiveSla,
  completedAt: string | null,
  referenceMs: number | null,
  completedBy: PrioritySlaDto['completedBy'] = null
): string => {
  const label = PRIORITY_SLA_STATE_LABELS[live.state]
  if (live.state === 'DONE') {
    if (completedBy === 'ANTECIPE_PAID') return PRIORITY_ANTECIPE_PAID_DONE_LABEL
    return completedAt ? `${label} ${formatSlaStamp(completedAt, referenceMs)}` : label
  }
  if (live.remainingSeconds === null) return label
  if (live.state === 'OVERDUE') return `${label} +${formatSlaShortDuration(live.remainingSeconds)}`
  if (live.state === 'URGENT' || live.state === 'ATTENTION') {
    return `${label} · ${formatSlaShortDuration(live.remainingSeconds)}`
  }
  return label
}

const deadlineDay = (sla: PrioritySlaDto): string =>
  formatDayMonthInOffset(sla.deadlineAt, DEFAULT_UTC_OFFSET) ?? '—'

/** Horas do SLA do Antecipe lidas do próprio SLA (prazo − início); `null` sem início. */
const antecipeHours = (sla: PrioritySlaDto): number | null => {
  if (!sla.startedAt) return null
  const ms = Date.parse(sla.deadlineAt) - Date.parse(sla.startedAt)
  return Number.isFinite(ms) ? Math.round(ms / 3_600_000) : null
}

export const formatAntecipeStartReason = (
  startedAt: string,
  requestedAt: string | null | undefined
): string => {
  if (!requestedAt) return 'fim do plantão · sem solicitação'
  return Date.parse(requestedAt) === Date.parse(startedAt) ? 'solicitação' : 'fim do plantão'
}

const notStartedDueText = (sla: PrioritySlaDto, requestedAt: string | null | undefined): string => {
  if (sla.kind === 'IN_CASH') {
    return `SLA começa no ciclo de ${deadlineDay(sla)} · envio até ${formatSlaHour(sla.deadlineAt)}`
  }
  const hours = antecipeHours(sla)
  if (!sla.startedAt || hours === null) return 'SLA do Antecipe ainda não começou'
  const reason = formatAntecipeStartReason(sla.startedAt, requestedAt)
  return `SLA de ${hours} h começa ${formatSlaDateTime(sla.startedAt)} (${reason})`
}

const doneDueText = (
  sla: PrioritySlaDto,
  situation: PrioritySituation,
  referenceMs: number | null
): string => {
  // Q1 — fora do time o SLA vem DONE sem `completedAt`: nada foi confirmado, só não é exigido.
  if (situation === 'NOT_REQUIRED') return 'registro não exigido'
  if (sla.completedBy === 'ANTECIPE_PAID') return PRIORITY_ANTECIPE_PAID_DONE_LABEL
  const verb = situation === 'RESOLVED_APP' ? 'check-out pelo app' : 'horário confirmado'
  return sla.completedAt ? `${verb} às ${formatSlaStamp(sla.completedAt, referenceMs)}` : verb
}

/** Linha do prazo absoluto embaixo do countdown (M3). */
export const formatSlaDueText = (
  sla: PrioritySlaDto,
  live: PriorityLiveSla,
  row: Pick<PriorityPaymentRow, 'situation' | 'antecipeRequestedAt'>,
  referenceMs: number | null
): string => {
  if (sla.state === 'NOT_STARTED') return notStartedDueText(sla, row.antecipeRequestedAt)
  if (sla.state === 'DONE') return doneDueText(sla, row.situation, referenceMs)
  return `${live.state === 'OVERDUE' ? 'prazo era' : 'prazo'} ${formatSlaDateTime(sla.deadlineAt)}`
}

/**
 * `'À vista · Envio 11:00'`, `'À vista · Auditoria 12:00'`, `'Antecipe · 12h'`.
 * Prazo do À vista fora de hoje (referência = relógio da tela) sai com o dia:
 * `'À vista · Envio seg 11:00'` (B1, 25/09). Atrasado de ciclo anterior mantém
 * `'… 11:00 de 23/09'`.
 */
export const formatSlaKindLabel = (
  sla: PrioritySlaDto,
  row: Pick<PriorityPaymentRow, 'overdue'>,
  referenceMs: number | null = null
): string => {
  if (sla.kind === 'ANTECIPE') {
    const hours = antecipeHours(sla)
    return hours === null ? 'Antecipe' : `Antecipe · ${hours}h`
  }
  if (sla.state === 'NOT_STARTED') return 'À vista · ciclo seguinte'
  const step = sla.stage === 'SEND' ? 'Envio' : 'Auditoria'
  if (row.overdue)
    return `À vista · ${step} ${formatSlaHour(sla.deadlineAt)} de ${deadlineDay(sla)}`
  return `À vista · ${step} ${formatSlaDeadlineStamp(sla.deadlineAt, referenceMs)}`
}

export const formatSlaStage = (sla: PrioritySlaDto, situation: PrioritySituation): string => {
  if (sla.stage === null) return 'Não iniciado'
  if (sla.stage === 'DONE') return 'Concluído'
  if (sla.stage === 'AUDIT') return 'Auditoria'
  return situation === 'REJECTED' ? 'Reenvio' : 'Envio'
}

/** Linha "etapa" da coluna "Tipo de SLA · etapa" da Tabela (M3). */
export const formatSlaStageLine = (sla: PrioritySlaDto, situation: PrioritySituation): string => {
  if (sla.stage === null) return 'aguardando fim do plantão'
  if (sla.stage === 'DONE') return 'concluído'
  return `etapa: ${formatSlaStage(sla, situation).toLowerCase()}`
}

export interface PrioritySlaAlso {
  kindLabel: string
  detail: string
}

/** "Também: <tipo> · <estado> · prazo dd/mm HH:mm" (M3). */
export const formatSlaAlso = (
  secondary: PrioritySlaDto,
  row: Pick<PriorityPaymentRow, 'overdue'>,
  nowMs: number | null,
  config: PriorityConfig | null | undefined
): PrioritySlaAlso => {
  const live = resolveLiveSla(secondary, nowMs, priorityThresholdsFor(config, secondary.kind))
  const deadline = isPendingSlaState(live.state)
    ? ` · prazo ${formatSlaDateTime(secondary.deadlineAt)}`
    : ''
  return {
    kindLabel: formatSlaKindLabel(secondary, row, nowMs),
    detail:
      live.state === 'DONE' && secondary.completedBy === 'ANTECIPE_PAID'
        ? PRIORITY_ANTECIPE_PAID_DONE_LABEL
        : `${PRIORITY_SLA_STATE_LABELS[live.state]}${deadline}`
  }
}

export const priorityAntecipeSla = (
  row: Pick<PriorityPaymentRow, 'sla'>
): PrioritySlaDto | null => {
  const sla = row.sla
  if (!sla) return null
  if (sla.kind === 'ANTECIPE') return sla
  return sla.secondary?.kind === 'ANTECIPE' ? sla.secondary : null
}

/** `'23/09 19:00 (fim do plantão)'`; `null` sem SLA de Antecipe. */
export const formatAntecipeStart = (
  row: Pick<PriorityPaymentRow, 'sla' | 'antecipeRequestedAt'>
): string | null => {
  const sla = priorityAntecipeSla(row)
  if (!sla?.startedAt) return null
  const reason = formatAntecipeStartReason(sla.startedAt, row.antecipeRequestedAt)
  return `${formatSlaDateTime(sla.startedAt)} (${reason})`
}

/**
 * Motivo de `row.critical`, como chip secundário. A3 (25/09): crítico é SÓ
 * "2+ rejeições" — Antecipe pago virou concluído (A1) e deixou de ser crítico.
 */
export const priorityCriticalReasons = (
  row: Pick<PriorityPaymentRow, 'activityRecord'>
): string[] => ((row.activityRecord?.rejectionCount ?? 0) >= 2 ? ['2+ rejeições'] : [])

/** Texto do selo "Só: …" da linha do relógio ligada (M3). */
export const formatSlaBandLabel = (
  band: PrioritySlaBand,
  config: PriorityConfig | null | undefined
): string => {
  if (band === 'IN_CASH_SEND') return `À vista · Envio até ${config?.sendDeadline ?? '11:00'}`
  if (band === 'IN_CASH_AUDIT') return `À vista · Auditoria até ${config?.auditDeadline ?? '12:00'}`
  return 'Antecipe pendentes'
}

const minutesAsText = (minutes: number): string =>
  minutes % 60 === 0 ? `${minutes / 60} h` : `${minutes} min`

/** Dica dos grupos Urgente/Atenção: `'À vista ≤15 min · Antecipe ≤2 h'`. */
export const formatThresholdHint = (
  config: PriorityConfig | null | undefined,
  level: 'urgent' | 'attention'
): string => {
  const key = level === 'urgent' ? 'urgentMinutes' : 'attentionMinutes'
  const inCash = priorityThresholdsFor(config, 'IN_CASH') ?? DEFAULT_THRESHOLDS.IN_CASH
  const antecipe = priorityThresholdsFor(config, 'ANTECIPE') ?? DEFAULT_THRESHOLDS.ANTECIPE
  return `À vista ≤${inCash[key]} min · Antecipe ≤${minutesAsText(antecipe[key])}`
}
